import type { MembershipStatus } from '../../../api/agwa-api-model';
import { terms } from '../../../language/terms';
import { STATE_LABELS } from '../../../utility/state-colours/state-colour-registry';

/**
 * One source for how a company's two independent states are presented: whether it may operate, and
 * whether it is a member. Shared by the members table, the member's own page, the Add dialog, the
 * legend that explains them, and the journeys that assert them.
 *
 * It lives here rather than in `view-groups.ts` because a legend that restates the labels is
 * exactly where drift starts: the table would say "Former member" and the legend "Lapsed member"
 * with nothing to catch it. Reading both from one map makes them incapable of disagreeing. The same
 * argument reaches the journeys, and it is not hypothetical here - a hard-coded operation-type
 * label in `chain-11` silently broke when the label was corrected, and nothing caught it because
 * verify.sh does not run Playwright.
 *
 * THESE ARE GLOSSARY TERMS, and this comment used to say the opposite. It claimed they name AGWA's
 * own classification rather than vocabulary, "the same reasoning D-030 applies to operation type
 * names". That was a false analogy, and citing a decision gave it authority it never had: D-030 is
 * about OPERATION TYPE names - Glass Processor, Prime System Manufacturer - which sit on a different
 * axis entirely (`UserGroupDomain`, what kind of business a company runs). It says nothing about the
 * word Member.
 *
 * Member is a core domain concept, spelled on the chips, the filter rail, the legend and the login
 * banner, and it is exactly the surface D-038 was waiting for when it said the second term "arrives
 * with the first surface that genuinely needs the word Member, and not before". It now comes from
 * `terms.member`; the standing itself is `terms.membership`; the company is `terms.loginGroup`.
 */

/**
 * What a company's OPERATING state is called on screen - the control that answers "may this company
 * work at all", as opposed to the membership classification below.
 *
 * A function rather than a map because the underlying value is a boolean, and a two-key record keyed
 * on `true` / `false` reads worse than the branch it is hiding.
 *
 * The off word is "Blocked", not "Inactive", as of D-036 stage 2 - the change landed in the same
 * commit that made the flag actually refuse a sign-in, which is the only point at which the word
 * became true. It matches what staff-list.ts already calls a blocked person, so the same fact reads
 * the same way whether it is about a person or the company they work for.
 */
export function operatingStatusLabel(active: boolean): string {
  return active ? 'Active' : 'Blocked';
}

/**
 * What each operating state MEANS. Only the legend uses these; they sit here so the word and its
 * explanation cannot be changed independently of each other.
 *
 * Both sentences describe behaviour that exists. Until D-036 stage 2 the legend deliberately carried
 * no operating-status rows at all, because "nobody at this company can sign in" would have been a
 * promise the software did not keep.
 */
export const OPERATING_STATUS_EXPLANATIONS: Array<{
  active: boolean;
  meaning: string;
}> = [
  {
    active: true,
    meaning: `${terms.loginUser.plural} at this ${terms.loginGroup.lower} can sign in and do their work.`,
  },
  {
    active: false,
    meaning: `Nobody at this ${terms.loginGroup.lower} can sign in, whatever their own account state. Anyone already signed in stops being able to work. Unblocking restores exactly what each ${terms.loginUser.lower} could do before, with nothing to set up again.`,
  },
];

/**
 * What each membership state is called on screen. The API's own words are internal identifiers and
 * "NeverMember" is not a phrase anyone would write, so the mapping lives here rather than being
 * rendered raw.
 *
 * The WORDS come from the state-colour registry, which owns every state label suite-wide (D-040 /
 * state-colour-standard.md) - agwa-state-badge renders them from there, and this map exists only
 * for the non-badge surfaces (dropdown options, filter labels) that need the same word as text.
 * Referencing the registry rather than re-composing from terms keeps the two incapable of
 * disagreeing. The old per-file colour maps (MEMBERSHIP_VARIANTS, operatingStatusVariant) are
 * gone for the same reason: colour is the registry's to decide.
 */
export const MEMBERSHIP_LABELS: Record<MembershipStatus, string> = {
  Member: STATE_LABELS.member.label,
  NeverMember: STATE_LABELS.notMember.label,
  FormerMember: STATE_LABELS.formerMember.label,
};

/**
 * What each state MEANS, as opposed to what it is called. Only the legend uses these, but they sit
 * beside the labels so that adding a fourth membership state cannot leave one of the three maps
 * behind - the Record type makes an omission a compile error.
 */
export const MEMBERSHIP_EXPLANATIONS: Record<MembershipStatus, string> = {
  Member: `Currently holds ${terms.agwa.value} ${terms.membership.lower}.`,
  NeverMember: `Recorded by ${terms.agwa.value}, but has never held ${terms.membership.lower}.`,
  FormerMember: `Held ${terms.membership.lower} and no longer does. Kept distinct from never having joined, because work they produced while they were a ${terms.member.lower} may still stand.`,
};

/**
 * Reading order for the legend, most-common first. Object key order would work today and would
 * silently change the screen the next time someone reorders the map, so the order is stated.
 */
export const MEMBERSHIP_ORDER: MembershipStatus[] = [
  'Member',
  'FormerMember',
  'NeverMember',
];

/**
 * Resolve whatever the API actually sent onto the canonical key the maps above are written in.
 *
 * THE SERVER DOES NOT SEND WHAT THE GENERATED MODEL SAYS IT SENDS, and that is a real defect rather
 * than a quirk to work around quietly. Observed on the running e2e server on 2026-08-08:
 *
 *     {"name":"DEV GLASS PROCESSOR","membershipStatus":"member", ...}
 *     {"name":"DEV GLASS AUDITOR","membershipStatus":"neverMember", ...}
 *
 * The wire format is camelCase, because `Program.cs` registers
 * `JsonStringEnumConverter(JsonNamingPolicy.CamelCase)`. The published OpenAPI schema says
 * PascalCase, because `StringEnumSchemaTransformer` emits `Enum.GetNames()` without applying that
 * naming policy - so `agwa-api-model.ts` declares `'Member' | 'NeverMember' | 'FormerMember'` and
 * the compiler cheerfully accepts a Record keyed on words the server never sends. This affects
 * EVERY enum in the API, not only this one; it is recorded in documentation/dod/ISSUE-324.md for
 * the architect, because correcting the spec regenerates every enum union in the client and is not
 * a change to make as a side effect of a members screen.
 *
 * Until then, reading has to normalise. That is the same reason CLAUDE.md requires `isSame()` for
 * state-like strings rather than `===`; a Record lookup is the one shape that cannot be made
 * case-insensitive, which is exactly how this shipped rendering an empty chip.
 *
 * THROWS on a value that matches nothing, deliberately, following `getOrganizationTypeLabel`. The
 * failure this replaces was silent - a blank chip that looked like a company with no membership
 * recorded rather than like a bug - and silence is what let it through review and a full chain run.
 */
export function toMembershipStatus(value: string): MembershipStatus {
  const match = MEMBERSHIP_ORDER.find(
    (status) => status.toLowerCase() === value?.toLowerCase()
  );
  if (match) return match;

  throw new Error(
    `Unknown membership status "${value}" from the API. Expected one of ${MEMBERSHIP_ORDER.join(', ')} in any casing. ` +
      `If a new state has been added server-side, it needs a label and colour in the state-colour registry, an explanation in company-status-display.ts, ` +
      `and a tier-1 scenario before it reaches a screen.`
  );
}

/** The on-screen label for whatever the API sent. */
export function membershipLabel(value: string): string {
  return MEMBERSHIP_LABELS[toMembershipStatus(value)];
}
